import { Card, CardBody, CardHeader, CardTitle, Alert } from '@/common'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import { AddAssetWithIdPayload, EmissionPointsEditorProps } from './useAssetInstances'
import { ProductionRouteAsset } from '@/features/production-routes/api/ProductionRouteModel'
import { StepFooter } from '@/shared/components'
import AssetCategoryEditor from './components/AssetCategoryEditor'
import { useTranslation } from 'react-i18next'
import HorizontalStudSoderbergIcon from '@/assets/horizontalStudSoderberg.svg?react'
import CenterWorkedPreBakeIcon from '@/assets/centerWorkedPreBake.svg?react'
import LegacyPointFeedPreBakeIcon from '@/assets/legacyPointFeedPreBake.svg?react'
import SideWorkedPreBakeIcon from '@/assets/sideWorkedPreBake.svg?react'
import VerticalStudSoderbergIcon from '@/assets/verticalStudSoderberg.svg?react'
import ModernPointFeedPreBakeIcon from '@/assets/modernPointFeedPreBake.svg?react'
import ModernPointfedPreBakeWithoutFullyAutomatedAnodeEffectIcon from '@/assets/modernPointfedPreBakeWithoutFullyAutomatedAnodeEffect.svg?react'
import { useState } from 'react'
import { EmissionsControlAssetEditor } from './components/EmissionsControlAssetEditor'

function RawMaterialPreperation({
  areAssetsUsed,
  selectedAssets,
  addAssetWithId,
  removeAssetWithId,
  updateAssetWithId,
  removeMultipleAssets,
  updateAreAssetsUsed,
  id
}: {
  areAssetsUsed?: boolean
  selectedAssets: ProductionRouteAsset[]
  addAssetWithId: (payload: AddAssetWithIdPayload) => void
  removeAssetWithId: (assetId: number) => void
  updateAssetWithId: (assetId: number, asset: ProductionRouteAsset) => void
  removeMultipleAssets: (ids: number[]) => void
  updateAreAssetsUsed?: (value: boolean) => void
  id: string
}) {
  const { t } = useTranslation()
  const { assetsById } = useAppConfig().config

  const assetDefinitions = [
    assetsById['crucible_channel_induction_furnace'],
    assetsById['crucible_induction_furnace'],
    assetsById['hearth_furnace.closed_well'],
    assetsById['hearth_furnace.round'],
    assetsById['hearth_furnace.top_loaded'],
    assetsById['reverberatory.barrel_shaped_furnace'],
    assetsById['reverberatory.oval_shaped']
  ]

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle title={t(`emissionPoints:raw_material_preparation.title`)} />
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert type="info" className="whitespace-pre-line">
            {t(`emissionPoints:raw_material_preparation.callout`)}
          </Alert>
          <AssetCategoryEditor
            booleanQuestion={{
              question: t(
                `emissionPoints:raw_material_preparation.assetCategories.pre_heating_or_drying_furnaces.booleanQuestion`
              ),
              value: areAssetsUsed || false,
              name: 'preHeatingOrDryingFurnaces',
              update: updateAreAssetsUsed
            }}
            title={t(
              `emissionPoints:raw_material_preparation.assetCategories.pre_heating_or_drying_furnaces.title`
            )}
            selectedAssets={selectedAssets}
            addAssetWithId={addAssetWithId}
            removeAssetWithId={removeAssetWithId}
            updateAssetWithId={updateAssetWithId}
            removeMultipleAssets={removeMultipleAssets}
            id={id}
            assetDefinitions={assetDefinitions}
            technologyTypeQuestion={t(
              `emissionPoints:raw_material_preparation.assetCategories.pre_heating_or_drying_furnaces.technologyTypeQuestion`
            )}
          />
        </CardBody>
      </Card>
    </>
  )
}

function ElectrolyticCellSystem({
  selectedAssets,
  addAssetWithId,
  removeAssetWithId,
  updateAssetWithId,
  removeMultipleAssets,
  id
}: {
  selectedAssets: ProductionRouteAsset[]
  addAssetWithId: (payload: AddAssetWithIdPayload) => void
  removeAssetWithId: (assetId: number) => void
  updateAssetWithId: (assetId: number, asset: ProductionRouteAsset) => void
  removeMultipleAssets: (ids: number[]) => void
  id: string
}) {
  const { t } = useTranslation()
  const { assetsById } = useAppConfig().config
  const assetDefinitions = [
    {
      ...assetsById['centre_worked_pre_bake'],
      icon: <CenterWorkedPreBakeIcon className="text-green-500 w-[100px] h-[100px]" />
    },
    {
      ...assetsById['side_worked_pre_bake'],
      icon: <SideWorkedPreBakeIcon className="text-green-500 w-[100px] h-[100px]" />
    },
    {
      ...assetsById['horizontal_stud_soderberg'],
      icon: <HorizontalStudSoderbergIcon className="text-green-500 w-[100px] h-[100px]" />
    },
    {
      ...assetsById['legacy_point_feed_pre_bake'],
      icon: <LegacyPointFeedPreBakeIcon className="text-green-500 w-[100px] h-[100px]" />
    },
    {
      ...assetsById['vertical_stud_soderberg'],
      icon: <VerticalStudSoderbergIcon className="text-green-500 w-[100px] h-[100px]" />
    },
    {
      ...assetsById['modern_point_feed_pre_bake'],
      icon: <ModernPointFeedPreBakeIcon className="text-green-500 w-[100px] h-[100px]" />
    },
    {
      ...assetsById['modern_pointfed_pre_bake_without_fully_automated_anode_effect'],
      icon: (
        <ModernPointfedPreBakeWithoutFullyAutomatedAnodeEffectIcon className="text-green-500 w-[100px] h-[100px]" />
      )
    }
  ]

  return (
    <Card>
      <CardHeader>
        <CardTitle title={t(`emissionPoints:electrolytic_cell_system.title`)} />
      </CardHeader>
      <CardBody className="space-y-4">
        <Alert type="info" className="whitespace-pre-line">
          {t(`emissionPoints:electrolytic_cell_system.callout`)}
        </Alert>
        <AssetCategoryEditor
          title={t(
            `emissionPoints:electrolytic_cell_system.assetCategories.electrolytic_cell_system.title`
          )}
          dialogTitle={t('productionRoute:emissionPoints.electrolyticCellSystem.addDialog.title')}
          selectedAssets={selectedAssets}
          addAssetWithId={addAssetWithId}
          removeAssetWithId={removeAssetWithId}
          updateAssetWithId={updateAssetWithId}
          removeMultipleAssets={removeMultipleAssets}
          isElectrolyticCellSystem
          id={id}
          assetDefinitions={assetDefinitions}
          technologyTypeQuestion={t(
            `emissionPoints:electrolytic_cell_system.assetCategories.electrolytic_cell_system.technologyTypeQuestion`
          )}
          selectionType={'icon'}
        />
      </CardBody>
    </Card>
  )
}

export function CastingSystem({
  selectedAssets,
  addAssetWithId,
  removeAssetWithId,
  updateAssetWithId,
  removeMultipleAssets,
  id
}: {
  selectedAssets: ProductionRouteAsset[]
  addAssetWithId: (payload: AddAssetWithIdPayload) => void
  removeAssetWithId: (assetId: number) => void
  updateAssetWithId: (assetId: number, asset: ProductionRouteAsset) => void
  removeMultipleAssets: (ids: number[]) => void
  id: string
}) {
  const { t } = useTranslation()
  const { assetsById } = useAppConfig().config
  const castingSystemRecommendedFurnaceAssetDefinitions = [
    assetsById['reverberatory.standard'],
    assetsById['crucible_furnace'],
    assetsById['crucible_induction_furnace'],
    assetsById['crucible_channel_induction_furnace'],
    assetsById['other_holding_furnace']
  ]

  const castingSystemOtherFurnaceAssetDefinitions = [
    assetsById['hearth_furnace.top_loaded'],
    assetsById['hearth_furnace.round'],
    assetsById['hearth_furnace.side_well'],
    assetsById['hearth_furnace.closed_well'],
    assetsById['hearth_furnace.dry_hearth'],
    assetsById['hearth_furnace.dry_hearth_with_bottom_heating'],
    assetsById['hearth_furnace.tower_furnace__shaft_furnace__stack_furnace'],
    assetsById['reverberatory.barrel_shaped_furnace'],
    assetsById['reverberatory.oval_shaped'],
    assetsById['rotary.fixed_axis'],
    assetsById['rotary.tilting'],
    assetsById['sweat_furnace'],
    assetsById['car_bottom_furnace'],
    assetsById['multichamber_furnace']
  ]

  const assetDefinitions = [
    ...castingSystemRecommendedFurnaceAssetDefinitions,
    ...castingSystemOtherFurnaceAssetDefinitions
  ]

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle title={t(`emissionPoints:casting_system.title`)} />
        </CardHeader>
        <CardBody className="space-y-4">
          <Alert type="info" className="whitespace-pre-line">
            {t(`emissionPoints:casting_system.callout`)}
          </Alert>
          <AssetCategoryEditor
            title={t(`emissionPoints:casting_system.assetCategories.casting_system.title`)}
            selectedAssets={selectedAssets}
            addAssetWithId={addAssetWithId}
            removeAssetWithId={removeAssetWithId}
            updateAssetWithId={updateAssetWithId}
            removeMultipleAssets={removeMultipleAssets}
            id={id}
            assetDefinitions={assetDefinitions}
            technologyTypeQuestion={t(
              `emissionPoints:casting_system.assetCategories.casting_system.technologyTypeQuestion`
            )}
          />
        </CardBody>
      </Card>
    </>
  )
}

export default function AluminiumPrimarySmeltingEmissionPointsEditor({
  addAssetWithId,
  removeAssetWithId,
  updateAssetWithId,
  removeMultipleAssets,
  selectedAssets,
  onPreviousClick,
  onNextClick
}: EmissionPointsEditorProps) {
  const [arePep1AssetsUsed, setArePep1AssetsUsed] = useState(false)
  const [arePep4AssetsUsed, setArePep4AssetsUsed] = useState(false)
  const pep1Assets = selectedAssets.filter(
    asset => asset.emissionPoint === 'raw_material_preparation'
  )
  const pep2Assets = selectedAssets.filter(
    asset => asset.emissionPoint === 'electrolytic_cell_system'
  )
  const pep3Assets = selectedAssets.filter(asset => asset.emissionPoint === 'casting_system')
  const pep4Assets = selectedAssets.filter(asset => asset.emissionPoint === 'emissions_control')

  const isNextEnabled =
    ((arePep1AssetsUsed && pep1Assets.length > 0) || !arePep1AssetsUsed) &&
    pep2Assets.length > 0 &&
    pep3Assets.length > 0 &&
    ((arePep4AssetsUsed && pep4Assets.length > 0) || !arePep4AssetsUsed)

  return (
    <div className="space-y-4">
      <RawMaterialPreperation
        selectedAssets={pep1Assets}
        areAssetsUsed={arePep1AssetsUsed}
        updateAreAssetsUsed={setArePep1AssetsUsed}
        addAssetWithId={addAssetWithId}
        removeAssetWithId={removeAssetWithId}
        updateAssetWithId={updateAssetWithId}
        removeMultipleAssets={removeMultipleAssets}
        id="raw_material_preparation"
      />
      <ElectrolyticCellSystem
        selectedAssets={pep2Assets}
        addAssetWithId={addAssetWithId}
        removeAssetWithId={removeAssetWithId}
        updateAssetWithId={updateAssetWithId}
        removeMultipleAssets={removeMultipleAssets}
        id="electrolytic_cell_system"
      />
      <CastingSystem
        selectedAssets={pep3Assets}
        addAssetWithId={addAssetWithId}
        removeAssetWithId={removeAssetWithId}
        updateAssetWithId={updateAssetWithId}
        removeMultipleAssets={removeMultipleAssets}
        id="casting_system"
      />
      <EmissionsControlAssetEditor
        selectedAssets={pep4Assets}
        areAssetsUsed={arePep4AssetsUsed}
        updateAreAssetsUsed={setArePep4AssetsUsed}
        addAssetWithId={addAssetWithId}
        removeAssetWithId={removeAssetWithId}
        updateAssetWithId={updateAssetWithId}
        removeMultipleAssets={removeMultipleAssets}
        id="emissions_control"
      />
      <StepFooter onPrevious={onPreviousClick} onNext={onNextClick} isNextEnabled={isNextEnabled} />
    </div>
  )
}
